import { useEffect, useState } from 'react';
import { Alert, Modal, Progress, message } from 'antd';
import DicomDropzone from './DicomDropzone';
import { useUploadStudy } from './hooks/useUploadStudy';
import { selectionLabel } from './utils/selection';
import styles from './UploadStudyModal.module.css';

interface UploadStudyModalProps {
  open: boolean;
  onClose: () => void;
  /** The upload was granted and its bytes started sending; the dialog closes. */
  onUploadStarted?: (uploadId: string) => void;
  onUploadSuccess?: (uploadId?: string) => void;
}

export default function UploadStudyModal({
  open,
  onClose,
  onUploadStarted,
  onUploadSuccess,
}: UploadStudyModalProps) {
  const [files, setFiles] = useState<File[]>([]);
  const [filesError, setFilesError] = useState(false);
  const [messageApi, contextHolder] = message.useMessage();
  const { upload, reset, state } = useUploadStudy();
  const isUploading = state.stage === 'requesting-url';

  // Reset state when the modal closes.
  useEffect(() => {
    if (!open) {
      setFiles([]);
      setFilesError(false);
      reset();
    }
  }, [open, reset]);

  // Clear the "files required" error as soon as files arrive.
  useEffect(() => {
    if (files.length > 0) setFilesError(false);
  }, [files]);

  const handleSubmit = async () => {
    if (files.length === 0) {
      setFilesError(true);
      messageApi.error('Please add at least one DICOM file or ZIP.');
      return;
    }
    const label = selectionLabel(files);
    let started = false;
    try {
      const result = await upload({
        files,
        // The rest of the transfer is followed on the Uploads page.
        onStarted: (uploadId) => {
          started = true;
          onUploadStarted?.(uploadId);
          onClose();
        },
      });
      if (result.failedCount > 0) {
        messageApi.warning(
          `${label}: ${result.failedCount} file(s) could not be uploaded.`
        );
      }
      onUploadSuccess?.(result?.uploadId);
    } catch {
      // Before the start the dialog shows the error (state.errorMessage);
      // after it the dialog is closed, so say it here.
      if (started) messageApi.error(`${label}: upload failed.`);
    }
  };

  return (
    <>
      {contextHolder}
      <Modal
        title="Upload study"
        open={open}
        onCancel={isUploading ? undefined : onClose}
        onOk={handleSubmit}
        okText="Upload study"
        okButtonProps={{
          loading: isUploading,
          disabled: files.length === 0,
        }}
        cancelButtonProps={{ disabled: isUploading }}
        width={560}
        destroyOnHidden
        centered
        maskClosable={!isUploading}
        wrapClassName={styles.belowHeaderWrap}
        styles={{
          body: {
            maxHeight: 'calc(100vh - var(--header-height, 64px) - 180px)',
            overflowY: 'auto',
            paddingRight: 20,
          },
        }}
      >
        <h4 className={styles.sectionLabel}>
          DICOM files <span className={styles.requiredMark}>*</span>
        </h4>
        <DicomDropzone
          files={files}
          onChange={setFiles}
          disabled={isUploading}
        />
        {filesError && (
          <div className={styles.filesErrorText}>
            Please add at least one DICOM file or ZIP.
          </div>
        )}

        {state.stage === 'requesting-url' && (
          <div className={styles.progressRow}>
            <Progress percent={0} status="active" format={() => 'Preparing…'} />
          </div>
        )}
        {state.stage === 'error' && state.errorMessage && (
          <Alert
            type="error"
            showIcon
            className={styles.errorAlert}
            message="Upload failed"
            description={state.errorMessage}
          />
        )}
      </Modal>
    </>
  );
}
